<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  trajectory: { type: Object, default: null },
  agentInfo: { type: Object, default: null },
  isInProgress: { type: Boolean, default: false },
});

const expandedSteps = ref(new Set());
const expandedTools = ref(new Set());

const steps = computed(() => props.trajectory?.steps || []);
const agentSteps = computed(() => steps.value.filter((s) => s.source === 'agent'));

const initExpanded = () => {
  expandedSteps.value = new Set();
  expandedTools.value = new Set();
};

// Reset only when switching to a different trajectory (null ↔ non-null),
// not on streaming updates that merely append new steps.
watch(
  () => props.trajectory,
  (newVal, oldVal) => {
    if (!oldVal || !newVal) {
      initExpanded();
    }
  },
  { immediate: true }
);

const toggleStep = (index) => {
  const next = new Set(expandedSteps.value);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  expandedSteps.value = next;
};

const toolKey = (stepIdx, toolIdx) => `${stepIdx}-${toolIdx}`;

const toggleTool = (stepIdx, toolIdx) => {
  const key = toolKey(stepIdx, toolIdx);
  const next = new Set(expandedTools.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expandedTools.value = next;
};

const getStepMetrics = (step) => step.metrics || step.usage || {};

const totalCost = computed(() => {
  let cost = 0;
  steps.value.forEach((s) => {
    const m = getStepMetrics(s);
    if (m.cost_usd) cost += m.cost_usd;
    else if (m.cost) cost += m.cost;
  });
  return cost;
});

const totalTokens = computed(() => {
  let tokens = 0;
  steps.value.forEach((s) => {
    const m = getStepMetrics(s);
    const t = m.total_tokens ?? ((m.prompt_tokens || 0) + (m.completion_tokens || 0));
    tokens += t;
  });
  return tokens;
});

const totalTime = computed(() => {
  if (props.trajectory?.final_metrics?.total_time != null) {
    return props.trajectory.final_metrics.total_time;
  }
  const timestamps = steps.value.map((s) => s.timestamp).filter(Boolean);
  if (timestamps.length < 2) return null;
  return (new Date(timestamps[timestamps.length - 1]) - new Date(timestamps[0])) / 1000;
});

const formatTotalTime = (seconds) => {
  if (seconds == null) return '';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  }
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return `${hrs}h ${mins}m`;
};

const formatCost = (val) => {
  if (!val) return '$0.00';
  return `$${Number(val).toFixed(4)}`;
};

const formatTokens = (val) => {
  if (!val) return '0';
  return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(val);
};

const truncateCommand = (cmd, maxLen = 80) => {
  if (!cmd || cmd.length <= maxLen) return cmd || '';
  return cmd.substring(0, maxLen) + '...';
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatDuration = (seconds) => {
  if (seconds == null || seconds < 0) return '';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

const getStepDuration = (idx) => {
  const step = steps.value[idx];
  if (!step?.timestamp) return null;
  const next = steps.value[idx + 1];
  if (next?.timestamp) {
    return (new Date(next.timestamp) - new Date(step.timestamp)) / 1000;
  }
  // Last step: use final_metrics total_time if available
  const totalTime = props.trajectory?.final_metrics?.total_time;
  const firstTs = steps.value[0]?.timestamp;
  if (totalTime != null && firstTs) {
    const elapsed = (new Date(step.timestamp) - new Date(firstTs)) / 1000;
    const remaining = totalTime - elapsed;
    return remaining > 0 ? remaining : null;
  }
  return null;
};

const getToolType = (tool) => {
  if (tool?.function?.name) return tool.function.name;
  if (tool?.function_name) return tool.function_name;
  if (tool?.type) return tool.type;
  return 'tool_call';
};

const parseToolArgs = (tool) => {
  const rawArgs = tool?.function?.arguments ?? tool?.arguments;
  if (rawArgs == null) return null;
  if (typeof rawArgs === 'string') {
    try {
      return JSON.parse(rawArgs);
    } catch {
      return rawArgs;
    }
  }
  return rawArgs;
};

const getToolCommand = (tool) => {
  const args = parseToolArgs(tool);
  if (args == null) return '';
  if (typeof args === 'string') return args;
  if (typeof args !== 'object') return String(args);
  if (args.command) return String(args.command);
  if (args.code) return String(args.code);
  if (args.keystrokes) return String(args.keystrokes);
  if (args.query) return String(args.query);
  try {
    return JSON.stringify(args, null, 2);
  } catch {
    return String(args);
  }
};

const formatToolObservation = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const getToolObservation = (tool) => {
  if (tool?.observation != null) return formatToolObservation(tool.observation);
  if (tool?.result != null) return formatToolObservation(tool.result);
  if (tool?.output != null) return formatToolObservation(tool.output);
  return '';
};

const getStepText = (step) => {
  // ATIF format uses `message` (string)
  if (step.message && typeof step.message === 'string') return step.message;
  // OpenAI-style format uses `content`
  if (step.content && typeof step.content === 'string') return step.content;
  if (Array.isArray(step.content)) {
    return step.content
      .filter((c) => c.type === 'text')
      .map((c) => c.text)
      .join('\n');
  }
  return '';
};

const getStepToolCalls = (step) => {
  if (step.tool_calls) return step.tool_calls;
  if (Array.isArray(step.content)) {
    return step.content.filter((c) => c.type === 'tool_use' || c.type === 'tool_call');
  }
  return [];
};

const getStepObservation = (step) => {
  const obs = step.observation;
  if (!obs) return '';
  if (typeof obs === 'string') return obs;
  if (obs.results && Array.isArray(obs.results)) {
    return obs.results
      .map((r) => (typeof r === 'string' ? r : r?.content || JSON.stringify(r)))
      .join('\n');
  }
  return formatToolObservation(obs);
};

</script>

<template>
  <div class="trajectory-viewer" data-testid="trajectory-viewer">
    <!-- Header -->
    <div class="trajectory-header">
      <div class="trajectory-header__info">
        <span v-if="agentInfo?.name" class="trajectory-badge trajectory-badge--agent">
          {{ agentInfo.name }}
        </span>
        <span v-if="agentInfo?.model_info?.name" class="trajectory-badge trajectory-badge--model">
          {{ agentInfo.model_info.name }}
        </span>
        <span v-if="isInProgress" class="trajectory-badge trajectory-badge--duration trajectory-badge--in-progress">
          <span class="trajectory-spinner"></span>
          IN PROGRESS
        </span>
      </div>
      <div class="trajectory-header__stats">
        <span v-if="totalTime != null" class="trajectory-stat">{{ formatTotalTime(totalTime) }}</span>
        <span class="trajectory-stat">{{ agentSteps.length }} steps</span>
        <span class="trajectory-stat">{{ formatTokens(totalTokens) }} tokens</span>
        <span class="trajectory-stat">{{ formatCost(totalCost) }}</span>
      </div>
    </div>

    <!-- Steps -->
    <div class="trajectory-steps">
      <div
        v-for="(step, idx) in steps"
        :key="idx"
        class="trajectory-step"
        :class="{
          'trajectory-step--user': step.source === 'user',
          'trajectory-step--agent': step.source === 'agent',
        }"
      >
        <!-- Step Header -->
        <button
          class="trajectory-step__header"
          @click="toggleStep(idx)"
          :data-testid="`trajectory-step-${idx}`"
        >
          <i
            class="mdi text-sm mr-2 transition-transform duration-200"
            :class="expandedSteps.has(idx) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
          ></i>
          <span v-if="step.source === 'user'" class="trajectory-step__label trajectory-step__label--user">
            Task Prompt
          </span>
          <template v-else>
            <span class="trajectory-step__label trajectory-step__label--agent">
              Step {{ steps.slice(0, idx + 1).filter(s => s.source === 'agent').length }}
            </span>
            <span v-if="step.timestamp" class="trajectory-step__time">
              {{ formatTimestamp(step.timestamp) }}
            </span>
            <span v-if="getStepDuration(idx) != null" class="trajectory-badge trajectory-badge--duration">
              {{ formatDuration(getStepDuration(idx)) }}
            </span>
          </template>
        </button>

        <!-- Step Body -->
        <div v-if="expandedSteps.has(idx)" class="trajectory-step__body">
          <!-- Text content -->
          <div v-if="getStepText(step)" class="trajectory-text">
            <pre class="trajectory-pre">{{ getStepText(step) }}</pre>
          </div>

          <!-- Tool calls -->
          <div
            v-for="(tool, tIdx) in getStepToolCalls(step)"
            :key="tIdx"
            class="trajectory-tool"
          >
            <button
              class="trajectory-tool__header"
              @click="toggleTool(idx, tIdx)"
              :data-testid="`trajectory-tool-${idx}-${tIdx}`"
            >
              <i
                class="mdi text-xs mr-1.5 transition-transform duration-200"
                :class="expandedTools.has(toolKey(idx, tIdx)) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
              ></i>
              <span class="trajectory-badge trajectory-badge--tool">{{ getToolType(tool) }}</span>
              <span class="trajectory-tool__preview">{{ truncateCommand(getToolCommand(tool)) }}</span>
            </button>

            <div v-if="expandedTools.has(toolKey(idx, tIdx))" class="trajectory-tool__body">
              <div v-if="getToolCommand(tool)" class="trajectory-command">
                <pre class="trajectory-command__text">{{ getToolCommand(tool) }}</pre>
              </div>
              <div v-if="getToolObservation(tool)" class="trajectory-observation">
                <div class="trajectory-observation__label">Output</div>
                <pre class="trajectory-observation__text">{{ getToolObservation(tool) }}</pre>
              </div>
            </div>
          </div>

          <!-- Step-level observation (ATIF format) -->
          <div v-if="getStepObservation(step)" class="trajectory-observation">
            <div class="trajectory-observation__label">Observation</div>
            <pre class="trajectory-observation__text">{{ getStepObservation(step) }}</pre>
          </div>

          <!-- Step metrics footer -->
          <div v-if="step.source === 'agent' && (getStepMetrics(step).prompt_tokens || getStepMetrics(step).total_tokens)" class="trajectory-metrics">
            <span class="trajectory-metric">
              {{ formatTokens((getStepMetrics(step).prompt_tokens || 0) + (getStepMetrics(step).completion_tokens || 0) || getStepMetrics(step).total_tokens) }} tokens
            </span>
            <span v-if="getStepMetrics(step).cost_usd || getStepMetrics(step).cost" class="trajectory-metric">
              {{ formatCost(getStepMetrics(step).cost_usd || getStepMetrics(step).cost) }}
            </span>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.trajectory-viewer {
  @apply flex flex-col gap-2 h-full overflow-y-auto text-sm;
  font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace, ui-monospace;
}

.trajectory-header {
  @apply flex items-center justify-between px-4 py-3 rounded-lg bg-white/5 border border-white/10;
}

.trajectory-header__info {
  @apply flex items-center gap-2;
}

.trajectory-header__stats {
  @apply flex items-center gap-3 text-xs text-white/50;
}

.trajectory-stat {
  @apply px-2 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/30;
}

.trajectory-badge {
  @apply inline-flex items-center px-2 py-0.5 rounded text-xs font-medium;
}

.trajectory-badge--agent {
  @apply bg-purple-500/20 text-purple-300 border border-purple-500/30;
}

.trajectory-badge--model {
  @apply bg-blue-500/20 text-blue-300 border border-blue-500/30;
}

.trajectory-badge--duration {
  @apply bg-green-500/20 text-green-300 border border-green-500/30 ml-auto;
}

.trajectory-badge--in-progress {
  @apply gap-1.5 ml-0;
}

.trajectory-badge--tool {
  @apply bg-amber-500/20 text-amber-300 border border-amber-500/30;
}

.trajectory-spinner {
  @apply w-3 h-3 rounded-full border-2 border-green-300/40 border-t-green-300 animate-spin;
}

.trajectory-steps {
  @apply flex flex-col gap-1;
}

.trajectory-step {
  @apply rounded-lg border border-white/10 overflow-hidden;
}

.trajectory-step--user {
  @apply border-blue-500/20;
}

.trajectory-step--agent {
  @apply border-white/10;
}

.trajectory-step__header {
  @apply flex items-center gap-2 w-full px-3 py-2 text-left bg-white/5 hover:bg-white/10 transition-colors duration-150 cursor-pointer;
}

.trajectory-step__label {
  @apply text-xs font-medium;
}

.trajectory-step__label--user {
  @apply text-blue-300;
}

.trajectory-step__label--agent {
  @apply text-white/80;
}

.trajectory-step__time {
  @apply text-xs text-white/40;
}

.trajectory-step__body {
  @apply px-4 py-3 flex flex-col gap-3 bg-black/20;
}

.trajectory-text {
  @apply rounded;
}

.trajectory-pre {
  @apply whitespace-pre-wrap break-words text-xs text-white/70 leading-relaxed m-0;
}

.trajectory-tool {
  @apply rounded border border-white/5 overflow-hidden;
}

.trajectory-tool__header {
  @apply flex items-center gap-2 w-full px-3 py-1.5 text-left bg-white/5 hover:bg-white/10 transition-colors duration-150 cursor-pointer;
}

.trajectory-tool__preview {
  @apply text-xs text-white/40 truncate;
}

.trajectory-tool__body {
  @apply flex flex-col gap-2 p-3 bg-black/30;
}

.trajectory-command {
  @apply rounded bg-black/40 p-3 border border-green-500/10;
}

.trajectory-command__text {
  @apply whitespace-pre-wrap break-words text-xs text-green-400 leading-relaxed m-0;
}

.trajectory-observation {
  @apply rounded bg-black/40 p-3 border border-white/5;
}

.trajectory-observation__label {
  @apply text-[10px] uppercase tracking-wider text-white/30 mb-1.5;
}

.trajectory-observation__text {
  @apply whitespace-pre-wrap break-words text-xs text-white/50 leading-relaxed m-0 max-h-64 overflow-y-auto;
}

.trajectory-metrics {
  @apply flex items-center gap-3 pt-2 border-t border-white/5 text-xs text-white/30;
}

.trajectory-metric {
  @apply px-1.5 py-0.5 rounded bg-white/5;
}

</style>
